import { LazyQueryHookOptions, QueryHookOptions, useLazyQuery, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { merge } from 'lodash-es';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import { useLocale } from 'state/intl';
import { useSanityGqlEndpoint } from 'state/network';

// Factory function to create the hook with a high level configuration and be able to reuse the generated hook
export function createUseQueryHook() {
  return function useSanityQuery<TData = any, TVariables = Record<string, any>>(
    query: DocumentNode,
    options: QueryHookOptions<TData, TVariables> = {}
  ) {
    const uri = useSanityGqlEndpoint();
    const { language, region } = useLocale();
    const prevLanguage = usePrevious(language);
    const prevRegion = usePrevious(region);

    const queryOptions = merge(options, { context: { uri } });

    const queryResult = useQuery<TData, TVariables>(query, queryOptions);

    const { refetch } = queryResult;

    useEffectOnUpdates(() => {
      if (prevLanguage !== language || prevRegion !== region) {
        refetch();
      }
    }, [prevLanguage, prevRegion, language, region, refetch]);

    return queryResult;
  };
}

// Factory function to create the hook with a high level configuration and be able to reuse the generated hook
export function createUseLazyQueryHook() {
  return function useLazySanityQuery<TData = any, TVariables = Record<string, any>>(
    query: DocumentNode,
    options: LazyQueryHookOptions<TData, TVariables> = {}
  ) {
    const uri = useSanityGqlEndpoint();

    const queryOptions = merge(options, { context: { uri } });

    return useLazyQuery<TData, TVariables>(query, queryOptions);
  };
}
